'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import { NavBack } from '@susu/headless-commerce/features/navigation/components/NavBack'
import { NavCloseButton } from '@susu/headless-commerce/features/navigation/components/NavCloseButton'
import ClientNavLanguageSelector from '@susu/headless-commerce/features/navigation/components/NavLanguageSelector/ClientNavLanguageSelector'
import navSubmenuStyles from '@susu/headless-commerce/features/navigation/components/NavSubmenu/NavSubmenu.module.scss'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { getLanguageFromLocale } from '@susu/headless-commerce/utils/localeUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackLanguageSelectorClicked } from '@susu/headless-commerce/utils/tracking/trackLanguageSelectorClicked'
import classNames from 'classnames'
import { Suspense, useCallback } from 'react'
import styles from './NavLocaleLanguage.module.scss'

export type ClientNavLocaleLanguageProps = {
  className?: string
}

export const ClientNavLocaleLanguage = ({
  className,
}: ClientNavLocaleLanguageProps) => {
  useSignals()

  const locale = useLocale()
  const country = useCountry()
  const { url, locationId } = URLData()
  const pageType = usePageType()
  const { openLanguageSelector, isLanguageSelectorOpened } = useClientNav()
  const localeLanguage = getLanguageFromLocale(locale)
  const [baseLang] =
    country.languages.indexOf(localeLanguage) > -1 ? [localeLanguage] : ['en']
  const languageNames = new Intl.DisplayNames([baseLang], {
    type: 'language',
  })
  const language = languageNames.of(baseLang)

  const handleButtonClick = useCallback(() => {
    openLanguageSelector()

    trackLanguageSelectorClicked({
      country,
      eventLocation: 'menu',
      locale,
      locationId,
      pageType,
      url,
    })
  }, [openLanguageSelector, country, locale, locationId, pageType, url])

  const buttonClass = classNames(
    'nav__locale-btn',
    'nav__locale-btn--language',
    styles['nav__locale-btn--language'],
    className,
  )

  const languageSelectorClass = classNames(
    styles['nav__language-selector'],
    'nav__language-selector',
    navSubmenuStyles['nav__submenu'],
    'nav__submenu',
    {
      [`${navSubmenuStyles['nav__submenu--opened']} nav__submenu--opened`]:
        isLanguageSelectorOpened.value,
    },
    className,
  )

  return (
    <>
      {country.languages.length > 1 ? (
        <button
          className={buttonClass}
          type="button"
          onClick={handleButtonClick}
          data-testid="nav-locale-language"
        >
          {language}
        </button>
      ) : (
        <span className={buttonClass} data-testid="nav-locale-language">
          {language}
        </span>
      )}
      <div className={languageSelectorClass}>
        <NavBack />
        <NavCloseButton className={'nav__submenu-close-btn'} />
        <Suspense>
          <ClientNavLanguageSelector />
        </Suspense>
      </div>
    </>
  )
}
